
// Geobloc Boundary Calculation Utility
// For drawing red squares around geoblocs during search

export interface GeoblocBoundary {
  geobloc: string;
  centerLat: number;
  centerLng: number;
  northEast: { lat: number; lng: number };
  southWest: { lat: number; lng: number };
  approximateRadius: number; // meters
}

// Plus Code grid cell sizes (approximate)
const PLUS_CODE_GRID_SIZES = {
  // Based on Plus Code precision levels
  8: 14,      // ~14m x 14m (8 character codes)
  10: 3.5,    // ~3.5m x 3.5m (10 character codes)
  11: 1,      // ~1m x 1m (11 character codes)
};

// Calculate approximate geobloc boundaries from Plus Code
export const calculateGeoblocBoundaries = (
  plusCode: string,
  centerCoordinates: { latitude: number; longitude: number }
): GeoblocBoundary => {
  // Remove '+' and determine precision
  const cleanCode = plusCode.replace(/\+/g, '');
  const precision = cleanCode.length;
  
  // Get grid cell size based on precision
  let cellSize = PLUS_CODE_GRID_SIZES[8]; // Default to 14m
  if (precision >= 11) cellSize = PLUS_CODE_GRID_SIZES[11];
  else if (precision >= 10) cellSize = PLUS_CODE_GRID_SIZES[10];
  
  // Since our geoblocs use the last 8 characters with 4-digit geo codes,
  // we create very small areas suitable for walking paths
  const smallCellSize = cellSize / 2; // Make even smaller for precision
  
  // Calculate degrees from meters (rough approximation)
  const latDelta = (smallCellSize / 111320); // ~111.32 km per degree latitude
  const lngDelta = (smallCellSize / (111320 * Math.cos(centerCoordinates.latitude * Math.PI / 180)));
  
  const geoblocId = extractGeoblocFromPlusCode(plusCode);
  
  console.log('Calculating geobloc boundaries:', {
    plusCode,
    precision,
    cellSize: smallCellSize,
    geoblocId,
    center: centerCoordinates,
    deltas: { latDelta, lngDelta }
  });
  
  return {
    geobloc: geoblocId,
    centerLat: centerCoordinates.latitude,
    centerLng: centerCoordinates.longitude,
    northEast: {
      lat: centerCoordinates.latitude + latDelta,
      lng: centerCoordinates.longitude + lngDelta
    },
    southWest: {
      lat: centerCoordinates.latitude - latDelta,
      lng: centerCoordinates.longitude - lngDelta
    },
    approximateRadius: smallCellSize
  };
};

// Extract geobloc identifier from Plus Code - FIXED LOGIC
// Geobloc should represent a larger area than the building-specific code
export const extractGeoblocFromPlusCode = (plusCode: string): string => {
  const cleanCode = plusCode.replace(/\+/g, '');
  
  // For global codes like "86P9V3MH+V4", extract the grid area code
  // Take the first 8 characters (grid area) instead of last 8 (precise location)
  if (cleanCode.length >= 8) {
    return cleanCode.substring(0, 8); // First 8 characters represent the larger grid area
  }
  
  // For shorter codes, use what we have
  return cleanCode;
};

// Check if coordinates are within geobloc boundaries
export const isWithinGeoblocBoundary = (
  coordinates: { latitude: number; longitude: number },
  boundary: GeoblocBoundary
): boolean => {
  return (
    coordinates.latitude >= boundary.southWest.lat &&
    coordinates.latitude <= boundary.northEast.lat &&
    coordinates.longitude >= boundary.southWest.lng &&
    coordinates.longitude <= boundary.northEast.lng
  );
};

// Find all addresses within the same geobloc
export const findAddressesInSameGeobloc = (
  targetGeobloc: string,
  allAddresses: any[]
): any[] => {
  return allAddresses.filter(address => {
    if (!address.qls_postal_code_generated) return false;
    return address.qls_postal_code_generated === targetGeobloc;
  });
};

// Check if coordinates are within a property boundary polygon using ray casting algorithm
export const isWithinPropertyBoundary = (
  coordinates: { latitude: number; longitude: number },
  boundaryCorners: { latitude: number; longitude: number; corner_sequence: number }[]
): boolean => {
  console.log('🔍 isWithinPropertyBoundary called with:', {
    coordinates,
    cornersCount: boundaryCorners.length,
    corners: boundaryCorners
  });

  if (boundaryCorners.length < 3) {
    console.log('❌ Not enough corners for polygon:', boundaryCorners.length);
    return false;
  }
  
  // Sort corners by sequence to ensure proper polygon order
  const sortedCorners = [...boundaryCorners].sort((a, b) => a.corner_sequence - b.corner_sequence);
  
  console.log('📐 Sorted corners:', sortedCorners.map(c => ({ 
    seq: c.corner_sequence, 
    lat: c.latitude, 
    lng: c.longitude 
  })));
  
  const x = coordinates.longitude;
  const y = coordinates.latitude;
  let inside = false;
  
  console.log('🎯 Testing point:', { x, y });
  
  // Ray casting algorithm (point in polygon test)
  for (let i = 0, j = sortedCorners.length - 1; i < sortedCorners.length; j = i++) {
    const xi = sortedCorners[i].longitude;
    const yi = sortedCorners[i].latitude;
    const xj = sortedCorners[j].longitude;
    const yj = sortedCorners[j].latitude;
    
    console.log(`🔄 Ray cast check ${i}-${j}:`, {
      i: { x: xi, y: yi },
      j: { x: xj, y: yj },
      condition1: (yi > y) !== (yj > y),
      condition2: x < (xj - xi) * (y - yi) / (yj - yi) + xi
    });
    
    if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)) {
      inside = !inside;
      console.log('🎯 Ray intersects edge, inside now:', inside);
    }
  }
  
  console.log('🏁 Final result: point is', inside ? 'INSIDE' : 'OUTSIDE', 'polygon');
  return inside;
};

// Calculate the area of a property boundary polygon in square meters.
//
// Method: project each corner into a local equirectangular (equidistant cylindrical)
// frame anchored at the polygon's mean latitude, then apply the shoelace formula.
// For parcel-sized polygons (<~1 km across) this matches a true geodesic/spherical
// area calculation to better than ~0.01%.
//
// Constants are WGS84 mean-degree lengths:
//   - 1° latitude  ≈ 111,132.954 m (mean meridian arc length)
//   - 1° longitude ≈ 111,319.488 m × cos(refLat) (equatorial circumference / 360)
export const calculatePropertyBoundaryArea = (
  boundaryCorners: { latitude: number; longitude: number; corner_sequence: number }[]
): number => {
  if (boundaryCorners.length < 3) return 0;

  // Sort by corner_sequence so the polygon traverses vertices in surveyor order.
  // Otherwise the shoelace formula would silently compute a self-intersecting "bowtie".
  const sortedCorners = [...boundaryCorners].sort(
    (a, b) => a.corner_sequence - b.corner_sequence
  );

  // Use a single reference latitude (the polygon's mean) for the longitude scale.
  // Using each corner's own cos(lat) would project corners onto slightly different
  // planes and warp the polygon for larger boundaries.
  const refLat =
    sortedCorners.reduce((sum, c) => sum + c.latitude, 0) / sortedCorners.length;

  const METERS_PER_DEG_LAT = 111132.954;
  const METERS_PER_DEG_LNG_EQUATOR = 111319.488;
  const lngToMeters = METERS_PER_DEG_LNG_EQUATOR * Math.cos((refLat * Math.PI) / 180);
  const latToMeters = METERS_PER_DEG_LAT;

  // Project each corner to local (x, y) meters relative to the first corner.
  const firstCorner = sortedCorners[0];
  const cornerMeters = sortedCorners.map((corner) => ({
    x: (corner.longitude - firstCorner.longitude) * lngToMeters,
    y: (corner.latitude - firstCorner.latitude) * latToMeters,
  }));

  // Shoelace formula: 2A = |Σ (x_i * y_{i+1} - x_{i+1} * y_i)|
  let twiceSignedArea = 0;
  for (let i = 0; i < cornerMeters.length; i++) {
    const j = (i + 1) % cornerMeters.length;
    twiceSignedArea += cornerMeters[i].x * cornerMeters[j].y;
    twiceSignedArea -= cornerMeters[j].x * cornerMeters[i].y;
  }

  return Math.abs(twiceSignedArea) / 2;
};

// Suggest 4 boundary corners as a square centered on the given address coordinates.
//
// Used by the Surveyor portal to seed an initial boundary that the surveyor can
// then refine on the map (drag) or in the form (edit lat/lng). The default size
// of 400 m² (~20m × 20m, ~4,306 ft²) is a realistic Liberian residential lot
// baseline; callers can override `sizeSqm` for larger/smaller parcels.
//
// Returns corners in CCW order (SW, SE, NE, NW) so the shoelace area in
// `calculatePropertyBoundaryArea` matches the underlying square exactly.
export const suggestPropertyBoundaryCorners = (
  centerLat: number,
  centerLng: number,
  sizeSqm: number = 400
): { latitude: number; longitude: number; corner_sequence: number }[] => {
  if (!Number.isFinite(centerLat) || !Number.isFinite(centerLng)) return [];
  const safeSize = Number.isFinite(sizeSqm) && sizeSqm > 0 ? sizeSqm : 400;

  // Half side length in meters for a square with the requested area.
  const halfSide = Math.sqrt(safeSize) / 2;

  // Convert meters → degrees using the same WGS84 constants used for area calc.
  const METERS_PER_DEG_LAT = 111132.954;
  const METERS_PER_DEG_LNG_EQUATOR = 111319.488;
  const cosLat = Math.cos((centerLat * Math.PI) / 180);
  const latOffset = halfSide / METERS_PER_DEG_LAT;
  const lngOffset = cosLat > 0 ? halfSide / (METERS_PER_DEG_LNG_EQUATOR * cosLat) : 0;

  return [
    { latitude: centerLat - latOffset, longitude: centerLng - lngOffset, corner_sequence: 1 }, // SW
    { latitude: centerLat - latOffset, longitude: centerLng + lngOffset, corner_sequence: 2 }, // SE
    { latitude: centerLat + latOffset, longitude: centerLng + lngOffset, corner_sequence: 3 }, // NE
    { latitude: centerLat + latOffset, longitude: centerLng - lngOffset, corner_sequence: 4 }, // NW
  ];
};

// Generate map overlay data for drawing red squares
export const generateGeoblocMapOverlay = (boundary: GeoblocBoundary) => {
  return {
    type: 'rectangle',
    coordinates: [
      [boundary.southWest.lng, boundary.southWest.lat], // SW
      [boundary.northEast.lng, boundary.southWest.lat], // SE
      [boundary.northEast.lng, boundary.northEast.lat], // NE
      [boundary.southWest.lng, boundary.northEast.lat], // NW
      [boundary.southWest.lng, boundary.southWest.lat]  // Close polygon
    ],
    style: {
      fillColor: 'rgba(255, 0, 0, 0.2)', // Semi-transparent red
      strokeColor: '#ff0000',             // Red border
      strokeWidth: 2
    },
    metadata: {
      geobloc: boundary.geobloc,
      radius: boundary.approximateRadius,
      suitable_for: ['walking_paths', 'bike_access', 'motorbike_access', 'foot_delivery']
    }
  };
};
